import { Chess } from "chess.js";
import { Evaluation, Mode, MoveWithComment, Settings, State } from "./models";
import { ScrollArea } from "@mantine/core";
import { useEffect, useRef } from "react";
import { useStorage } from "./ZustandState";
import cn from "classnames";
import _ from "lodash";

interface HistoryTableProps {
    game: Chess;
    settings: Settings;
    state: State;
    mode: Mode;
}

export const getHistory = (moveHistory: MoveWithComment[], evaluations: Record<number, Evaluation>) => {
    const moves = [];

    const blackStarted = false;

    if (blackStarted) {
        moves.push({ move: "-" });
    }

    const evalByFen = new Map(Object.values(evaluations).map((e) => [e.fen, e]));

    for (let i = 0; i < moveHistory.length; i++) {
        const move = moveHistory[i].san;
        const evaluation = evalByFen.get(moveHistory[i].after);

        moves.push({
            move,
            evaluation: evaluation?.evaluation || "-",
            judgement: evaluation?.judgement,
            bestMove: evaluation?.bestMove,
            isPositionInRepo: evaluation?.isPositionInRepo,
        });
    }

    // // Add last eval to last move (in case it ends after a white move)
    // const sortedKeys = Object.keys(evaluations)
    //     .map(Number)
    //     .sort((a, b) => a - b);
    // const lastEval = sortedKeys.length > 0 ? evaluations[sortedKeys[sortedKeys.length - 1]] : undefined;
    // if (!_.isEmpty(moves) && lastEval) {
    //     moves[moves.length - 1].evaluation = lastEval.evaluation;
    // }
    return moves;
};

const HistoryTable: React.FC<HistoryTableProps> = ({ game, settings, state, mode }) => {
    const moveHistory = useStorage((state) => state.moveHistory);
    const evaluations = useStorage((state) => state.evaluations);
    const redoStack = useStorage((state) => state.redoStack);
    const setRedoStack = useStorage((state) => state.setRedoStack);
    const playerColor = useStorage((state) => state.playerColor);
    const showEval = (mode === "practice" && settings.showEval) || state === "attempt_finished";
    const history = getHistory(moveHistory, evaluations);
    const setArrowToDraw = useStorage((state) => state.setArrowToDraw);
    const rows = [];

    const currentMove = game.history().length;

    const viewport = useRef<HTMLDivElement>(null);

    const scrollToBottom = () =>
        viewport.current?.scrollTo({ top: viewport.current!.scrollHeight, behavior: "instant" });

    useEffect(() => {
        scrollToBottom();
    }, [moveHistory]);

    const undoOrRedo = (numUndos: number) => {
        // undo
        if (numUndos > 0) {
            const newRedoStack = redoStack.slice(0, redoStack.length);
            for (let j = 0; j < numUndos; j++) {
                const lastMove = _.last(game?.history({ verbose: true }));
                if (lastMove) {
                    newRedoStack.push(lastMove);
                    game?.undo();
                }
            }
            setRedoStack(newRedoStack);
        }

        // redo
        if (numUndos < 0) {
            const newRedoStack = redoStack.slice(0, redoStack.length);
            for (let j = 0; j < -numUndos; j++) {
                const lastMove = newRedoStack.pop();
                if (lastMove) {
                    game?.move(lastMove);
                }
            }
            setRedoStack(newRedoStack);
        }
    };

    const attemptFinishedOrTraining = state === "attempt_finished" || mode === "practice";
    const shouldDisplayWhiteMistakes =
        playerColor === "w" && attemptFinishedOrTraining && settings.mistakeMode !== "none";
    const shouldDisplayBlackMistakes =
        playerColor === "b" && attemptFinishedOrTraining && settings.mistakeMode !== "none";
    const shouldDisplayWhiteInaccuracies = shouldDisplayWhiteMistakes && settings.mistakeMode === "all";
    const shouldDisplayBlackInaccuracies = shouldDisplayBlackMistakes && settings.mistakeMode === "all";

    for (let i = 0; i < history.length; i += 2) {
        const whiteMove = history[i];
        const blackMove = history[i + 1];
        let ev = blackMove?.evaluation || whiteMove.evaluation;
        if (ev === "-") {
            ev = "";
        }

        const row = (
            <div key={i} className="grid grid-cols-[1fr_2fr_2fr_2fr]">
                <div className="justify-self-start">{i / 2 + 1}.</div>
                <div
                    className={cn("text-left pl-2", {
                        "backdrop-brightness-[200%]": currentMove === i + 1 && state === "attempt_finished",
                        "hover:backdrop-brightness-[200%] hover:cursor-pointer": state === "attempt_finished",
                        "text-[#167bb6]": shouldDisplayWhiteInaccuracies && whiteMove?.judgement === "inaccuracy",
                        "text-orange-300": shouldDisplayWhiteMistakes && whiteMove?.judgement === "mistake",
                        "text-[#ff8787]": shouldDisplayWhiteMistakes && whiteMove?.judgement === "blunder",
                        "text-green-400": attemptFinishedOrTraining && whiteMove.isPositionInRepo,
                    })}
                    onClick={() => {
                        if (state === "attempt_finished") {
                            const numUndos = game.history().length - i - 1;
                            undoOrRedo(numUndos);

                            const previousMove = history[i - 1];
                            const nextMove = history[i + 1];
                            const moveWithEvaluation = history[i];

                            if (!!moveWithEvaluation?.judgement && previousMove.bestMove) {
                                const firstMove = previousMove.bestMove.slice(0, 2);
                                const secondMove = previousMove.bestMove.slice(2, 4);
                                setArrowToDraw({
                                    from: firstMove,
                                    to: secondMove,
                                });
                            } else if (nextMove?.judgement && moveWithEvaluation.bestMove) {
                                const firstMove = moveWithEvaluation.bestMove.slice(0, 2);
                                const secondMove = moveWithEvaluation.bestMove.slice(2, 4);
                                setArrowToDraw({
                                    from: firstMove,
                                    to: secondMove,
                                });
                            } else {
                                setArrowToDraw(undefined);
                            }
                        }
                    }}
                >
                    <span>
                        {whiteMove.move}
                        {shouldDisplayWhiteMistakes && whiteMove?.judgement !== undefined ? "?" : ""}
                    </span>
                </div>
                <div
                    className={cn("text-left pl-2 ", {
                        "backdrop-brightness-[200%]": currentMove === i + 2 && state === "attempt_finished",
                        "hover:backdrop-brightness-[200%] hover:cursor-pointer":
                            state === "attempt_finished" && blackMove,
                        "text-[#167bb6]": shouldDisplayBlackInaccuracies && blackMove?.judgement === "inaccuracy",
                        "text-orange-300": shouldDisplayBlackMistakes && blackMove?.judgement === "mistake",
                        "text-[#ff8787]": shouldDisplayBlackMistakes && blackMove?.judgement === "blunder",
                        "text-green-400": attemptFinishedOrTraining && blackMove?.isPositionInRepo,
                    })}
                    onClick={() => {
                        if (state === "attempt_finished") {
                            const numUndos = game.history().length - i - 2;
                            undoOrRedo(numUndos);

                            const previousMove = history[i];
                            const nextMove = history[i + 2];
                            const moveWithEvaluation = history[i + 1];

                            if (previousMove.bestMove && !!blackMove?.judgement) {
                                console.log("BEST MOVE IS ", previousMove.bestMove);
                                const firstMove = previousMove.bestMove.slice(0, 2);
                                const secondMove = previousMove.bestMove.slice(2, 4);
                                setArrowToDraw({
                                    from: firstMove,
                                    to: secondMove,
                                });
                            } else if (nextMove?.judgement && moveWithEvaluation.bestMove) {
                                const firstMove = moveWithEvaluation.bestMove.slice(0, 2);
                                const secondMove = moveWithEvaluation.bestMove.slice(2, 4);
                                setArrowToDraw({
                                    from: firstMove,
                                    to: secondMove,
                                });
                            } else {
                                setArrowToDraw(undefined);
                            }
                        }
                    }}
                >
                    {blackMove && (
                        <span>
                            {blackMove.move}
                            {shouldDisplayBlackMistakes && blackMove?.judgement !== undefined ? "?" : ""}
                        </span>
                    )}
                </div>
                {showEval && <div className="text-right">{ev}</div>}
            </div>
        );

        rows.push(row);
    }

    return (
        <div className="mt-6 h-52 [@media(min-width:1023px)]:w-64 [@media(min-height:750px)]:h-64 [@media(min-height:870px)]:h-80  px-4 py-2 bg-[#1f2025] rounded-lg">
            <div className="flex justify-between">
                <div>Moves</div>
                {showEval && <div>Eval</div>}
            </div>
            <hr />
            <ScrollArea className="mt-1" h="85%" viewportRef={viewport} offsetScrollbars>
                {rows}
            </ScrollArea>
        </div>
    );
};

export default HistoryTable;
